import { useState } from 'react';
import { useSelector } from 'react-redux';

import { IconNames } from '@shipengine/giger-theme';
import { Icon, IconSize } from '@shipengine/giger';
import { useTranslation } from '@packlink/translation-provider';
import { useTenantConfig } from '@packlink/tenant-config-provider';
import { BrandAssetType, BrandLogo, useAssetUrl } from '@packlink/tenant-theme-provider';

import { AmplitudeEvents, useAmplitude } from '@metrics';
import { LanguageSelector } from '@components/LanguageSelector';
import { getAvailableLanguages, getLocale } from '@store/selectors';

import { getIconStyles } from './HeaderStyles';

export const Header = (): JSX.Element => {
    const { t } = useTranslation();

    const sendAmplitudeEvent = useAmplitude();
    const getLogo = useAssetUrl(BrandAssetType.LOGO);

    const currentLocale = useSelector(getLocale);
    const currentLanguage = useSelector(getAvailableLanguages).find((language) => language.locale === currentLocale);
    const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);
    const {
        hasHeaderTitle,
        externalPages: {
            home: homeUrl,
            bulk_url: { pending_shipments: pendingShipmentsUrl },
        },
        support: { url: supportUrl },
    } = useTenantConfig<ITenantConfig>();

    const gotToBulk = (): void => {
        sendAmplitudeEvent(AmplitudeEvents.PRINT_LABEL_IN_BULK);
        window.location.href = pendingShipmentsUrl;
    };

    const openLanguageSelector = (): void => {
        setIsLanguageSelectorOpen(true);
    };

    const closeLanguageSelector = (): void => {
        setIsLanguageSelectorOpen(false);
    };

    const sendCustomersHeaderEvent = (): void => {
        sendAmplitudeEvent(AmplitudeEvents.CLICK_CUSTOMER_SERVICE_HEADER);
    };

    return (
        <>
            <header className="header">
                <a href={homeUrl} className="header__logo" aria-label={t('header.logo.aria-label')}>
                    <img
                        className="header__logo__image"
                        src={getLogo(BrandLogo.LOGO_ALT)}
                        alt={t('header.logo.img-alt')}
                    />
                </a>
                {hasHeaderTitle && <h1 className="header__title">{t('header.logo.title')}</h1>}
                <ul className="header__list">
                    <li>
                        <a onClick={openLanguageSelector} role="button">
                            <Icon css={getIconStyles} name={IconNames.WORLD} size={IconSize.SIZE_SMALL} />
                            {t('header.language-selector.language')} · {currentLanguage?.language}
                        </a>
                    </li>

                    <li>
                        <a onClick={gotToBulk} aria-label={t('header.bulk.aria-label')} role="button">
                            {t('header.bulk.label')}
                        </a>
                    </li>

                    <li>
                        <a
                            onClick={sendCustomersHeaderEvent}
                            href={supportUrl}
                            aria-label={t('header.support.aria-label')}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            {t('header.support.label')}
                        </a>
                    </li>
                </ul>
            </header>
            <LanguageSelector isOpen={isLanguageSelectorOpen} close={closeLanguageSelector} />
        </>
    );
};
